// LeftoverCapture.tsx
//
// "Leftovers?" card on the post-cook Session Summary (Plan 14, Daily
// Surfaces — Task 5). Records a leftover-portion count on the just-completed
// cook log (PATCH /cook-logs/:id) and — only when the cook session was
// started FROM a plan slot (`session.planRef` present) — offers a one-tap
// follow-up to fill an empty slot on TOMORROW's plan with these leftovers.
//
// "Tomorrow" is derived the same way `planLinkage.ts`/`useTonight` derive
// "today": `localDateInZone(householdTz, new Date())` then `addCalendarDays`.
// Reading tomorrow's plan goes through `useMealPlan`, whose `buildEmptyWeek`
// fallback guarantees all 7 days x 3 slots exist to read even when the week
// hasn't synced down yet (or doesn't exist server-side at all).
//
// Writing the plan uses `setSlotLeftover`, which always addresses the target
// slot by (date, mealType) POSITION via `editWeek` — never by a captured id
// from this component's own (possibly-fallback) rendering of tomorrow's
// week. `editWeek` lazy-creates the doc if needed, so this works correctly
// even when the cook completes on a Sunday and "tomorrow" (Monday) falls in
// a week whose document doesn't exist client-side yet.
import type { JSX } from "react";
import { useState } from "react";
import {
  addCalendarDays,
  localDateInZone,
  mondayOf,
  type CookSessionApiItem,
  type MealType,
} from "@recipevault/shared";
import { patchCookLog } from "./cook-log-api.js";
import { setSlotLeftover } from "../meal-plans/editWeek.js";
import { useMealPlan } from "../meal-plans/useMealPlan.js";
import { useHouseholdTimezone } from "../auth/useHouseholdTimezone.js";
import { friendlyError } from "../../shared/http.js";
import { cn } from "../../shared/utils.js";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PORTION_CHIPS: ReadonlyArray<{ label: string; value: number }> = [
  { label: "None", value: 0 },
  { label: "2", value: 2 },
  { label: "4", value: 4 },
  { label: "6+", value: 6 },
];

// The follow-up only ever targets lunch/dinner — the design's "plan
// tomorrow's lunch/dinner with the leftovers" doesn't cover breakfast.
const FOLLOWUP_MEALS: readonly MealType[] = ["lunch", "dinner"];

const MEAL_LABEL: Record<MealType, string> = {
  breakfast: "breakfast",
  lunch: "lunch",
  dinner: "dinner",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface LeftoverCaptureProps {
  logId: string;
  /** SK prefix `patchCookLog` needs to address the row (thisLog.completedAt). */
  completedAt: string;
  session: CookSessionApiItem;
  recipeTitle: string;
  /** Persisted value from the cook log, if any — seeds the initial chip so a page refresh reflects what was already recorded. */
  leftoverPortions: number | null;
}

export function LeftoverCapture({
  logId,
  completedAt,
  session,
  recipeTitle,
  leftoverPortions,
}: LeftoverCaptureProps): JSX.Element {
  const tz = useHouseholdTimezone();
  const today = localDateInZone(tz, new Date());
  const tomorrow = addCalendarDays(today, 1);
  const tomorrowWeekStart = mondayOf(tomorrow);
  const tomorrowPlan = useMealPlan(tomorrowWeekStart);

  const [portions, setPortions] = useState<number | null>(leftoverPortions);
  const [patchError, setPatchError] = useState<string | null>(null);
  const [planned, setPlanned] = useState<Partial<Record<MealType, boolean>>>({});
  const [planError, setPlanError] = useState<string | null>(null);

  function handleChipSelect(value: number): void {
    const prev = portions;
    setPortions(value);
    setPatchError(null);
    void patchCookLog(logId, { completedAt, leftoverPortions: value }).catch((err: unknown) => {
      setPortions(prev); // revert on failure
      setPatchError(friendlyError(err));
    });
  }

  function handlePlanTomorrow(mealType: MealType): void {
    if (!session.planRef) return; // defensive — button isn't rendered unlinked
    const planRef = session.planRef;
    setPlanError(null);
    // OMIT `label` (rather than send "") when the recipe title hasn't resolved
    // from useRecipe yet: LeftoverRefSchema.label is .min(1), so a present-but-
    // empty label fails the whole parse and the write is rejected client-side.
    // `slotId` alone satisfies the schema's refine. Mirrors addFreetextToSlot's
    // "omit an empty optional" precedent in editWeek.ts.
    const leftoverOf = {
      slotId: planRef.slotId,
      ...(recipeTitle.trim().length > 0 ? { label: recipeTitle.slice(0, 200) } : {}),
    };
    void setSlotLeftover(tomorrowWeekStart, tomorrow, mealType, leftoverOf)
      .then(() => {
        setPlanned((prev) => ({ ...prev, [mealType]: true }));
      })
      .catch((err: unknown) => {
        setPlanError(friendlyError(err));
      });
  }

  const tomorrowDay = tomorrowPlan.days.find((d) => d.date === tomorrow);

  function isSlotEmpty(mealType: MealType): boolean {
    const slot = tomorrowDay?.slots.find((s) => s.mealType === mealType);
    if (!slot) return false;
    return slot.skipped !== true && slot.items.length === 0 && slot.leftoverOf === undefined;
  }

  const showFollowUp = portions !== null && portions > 0 && session.planRef !== undefined;
  // Once a slot is successfully planned, keep showing it (as "Planned ✓")
  // even though the live read of `tomorrowPlan` may not reflect the write
  // yet (e.g. it never will in a test that mocks `setSlotLeftover`).
  const followUpMeals = showFollowUp
    ? FOLLOWUP_MEALS.filter((m) => planned[m] === true || isSlotEmpty(m))
    : [];

  return (
    <section className="w-full max-w-md flex flex-col gap-2">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
        Leftovers?
      </h2>
      <div
        className="inline-flex flex-wrap items-center gap-1.5"
        role="group"
        aria-label="Leftover portions"
      >
        {PORTION_CHIPS.map(({ label, value }) => {
          const active = portions === value;
          return (
            <button
              key={label}
              type="button"
              aria-pressed={active}
              onClick={() => { handleChipSelect(value); }}
              className={cn(
                "inline-flex min-h-12 min-w-12 items-center justify-center rounded-full border px-4 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50 transition-colors",
                active
                  ? "bg-emerald-700/30 border-emerald-600 text-emerald-200"
                  : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-600",
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
      {patchError !== null && <p className="text-red-400 text-xs">{patchError}</p>}

      {followUpMeals.length > 0 && (
        <div className="flex flex-col gap-2 pt-1">
          {followUpMeals.map((mealType) => {
            const isPlanned = planned[mealType] === true;
            return (
              <button
                key={mealType}
                type="button"
                disabled={isPlanned}
                onClick={() => { handlePlanTomorrow(mealType); }}
                className={cn(
                  "min-h-12 rounded-lg border px-4 text-sm font-semibold transition-colors",
                  isPlanned
                    ? "bg-emerald-900/30 border-emerald-800 text-emerald-400 cursor-default"
                    : "bg-emerald-600 hover:bg-emerald-500 border-emerald-600 text-white",
                )}
              >
                {isPlanned ? "Planned ✓" : `Plan tomorrow's ${MEAL_LABEL[mealType]}`}
              </button>
            );
          })}
          {planError !== null && <p className="text-red-400 text-xs">{planError}</p>}
        </div>
      )}
    </section>
  );
}
